"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import FrayerSheet from "@/components/tools/games-organizers/frayer-sheet";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  EMPTY_MODEL,
  hasModel,
  MAX_TERMS,
  type Model,
  parseTerms,
  SQUARES,
  type Square,
} from "@/lib/tool-kit/games-organizers/frayer";
import {
  type Direction,
  drawingRatio,
  type Paper,
} from "@/lib/tool-kit/games-organizers/sheet";
import { worksheetPrintCss } from "@/lib/tool-kit/worksheets/layout";

/** English strings. A translated content file overrides these through `ui`. */
export const FRAYER_MODEL_GENERATOR_STRINGS = {
  termsLabel: "Terms — one per line, one organizer for each",
  termsHint: "Up to {max} terms.",
  termsPlaceholder: "photosynthesis\necosystem\nadaptation",
  exampleTerms: "photosynthesis\necosystem\nadaptation",
  loadExample: "Load example terms",
  modelHeading: "Model entry for the first term (optional)",
  modelHint:
    "Fill in any squares to show students a finished organizer, one idea per line. Leave them empty for blank squares to write in.",
  modelExample: {
    definition: "The process by which plants use light to make food",
    characteristics:
      "Needs light, water and carbon dioxide\nHappens in chloroplasts\nReleases oxygen",
    examples: "A leaf making glucose in sunlight\nAlgae in a pond",
    nonExamples: "Respiration\nDigesting a meal",
  },
  loadModel: "Load example entry",
  squareLabels: {
    definition: "Definition square",
    characteristics: "Characteristics square",
    examples: "Examples square",
    nonExamples: "Non-examples square",
  },
  headingSetLabel: "Square headings",
  headingSets: {
    classic: {
      definition: "Definition",
      characteristics: "Characteristics",
      examples: "Examples",
      nonExamples: "Non-examples",
    },
    plain: {
      definition: "What it means",
      characteristics: "Facts and features",
      examples: "Examples",
      nonExamples: "Non-examples",
    },
  },
  headingSetOptions: {
    classic: "Definition, Characteristics",
    plain: "What it means, Facts and features",
  },
  paperLabel: "Paper",
  papers: { letter: "US Letter", a4: "A4" },
  directionLabel: "Orientation",
  directions: { landscape: "Landscape", portrait: "Portrait" },
  ruled: "Draw writing lines in empty squares",
  nameLine: "Name and date line",
  print: "Print organizers",
  heading: "Your organizers",
  count: "one:{count} organizer|other:{count} organizers",
  needTerm: "Enter at least one term to see the organizer.",
  repeats: "Repeated terms were used once: {count}.",
  overflow: "Only the first {max} terms are used.",
  clipped: "Long terms were shortened: {count}.",
  sheetLabel: "Frayer model for {term}",
  sheetTitle: "Frayer Model",
  name: "Name",
  date: "Date",
} as const;

type Strings = typeof FRAYER_MODEL_GENERATOR_STRINGS;
type HeadingSet = keyof Strings["headingSets"];

const SHEET_ORDER = SQUARES;

function ModelFields({
  model,
  strings,
  onChange,
}: {
  model: Model;
  strings: Strings;
  onChange: (square: Square, value: string) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {SHEET_ORDER.map((square) => (
        <TextAreaField
          id={`frayer-model-${square}`}
          key={square}
          label={strings.squareLabels[square]}
          onChange={(value) => onChange(square, value)}
          prose
          rows={4}
          value={model[square]}
        />
      ))}
    </div>
  );
}

export default function FrayerModelGeneratorWidget() {
  const strings = useToolUi(FRAYER_MODEL_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [model, setModel] = useState<Model>(EMPTY_MODEL);
  const [headingSet, setHeadingSet] = useState<HeadingSet>("classic");
  const [paper, setPaper] = useState<Paper>("letter");
  const [direction, setDirection] = useState<Direction>("landscape");
  const [ruled, setRuled] = useState(true);
  const [nameLine, setNameLine] = useState(true);

  const parsed = parseTerms(text);
  const ratio = drawingRatio(paper, direction);
  const headings = strings.headingSets[headingSet];

  return (
    <div className="space-y-6">
      <style>{worksheetPrintCss(paper, direction)}</style>
      <div className="space-y-4 print:hidden">
        <TextAreaField
          hint={t(strings.termsHint, { max: MAX_TERMS })}
          id="frayer-terms"
          label={strings.termsLabel}
          onChange={setText}
          placeholder={strings.termsPlaceholder}
          prose
          rows={5}
          value={text}
        />
        <button
          className={BUTTON_SMALL}
          onClick={() => setText(strings.exampleTerms)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <fieldset className="space-y-3">
          <legend className="font-medium text-foreground text-sm">
            {strings.modelHeading}
          </legend>
          <p className={HINT}>{strings.modelHint}</p>
          <ModelFields
            model={model}
            onChange={(square, value) =>
              setModel((current) => ({ ...current, [square]: value }))
            }
            strings={strings}
          />
          <button
            className={BUTTON_SMALL}
            onClick={() => setModel({ ...strings.modelExample })}
            type="button"
          >
            {strings.loadModel}
          </button>
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="frayer-headings"
            label={strings.headingSetLabel}
            onChange={setHeadingSet}
            options={(["classic", "plain"] as const).map((value) => ({
              value,
              label: strings.headingSetOptions[value],
            }))}
            value={headingSet}
          />
          <PillGroup
            label={strings.paperLabel}
            name="frayer-paper"
            onChange={setPaper}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={paper}
          />
          <PillGroup
            label={strings.directionLabel}
            name="frayer-direction"
            onChange={setDirection}
            options={(["landscape", "portrait"] as const).map((value) => ({
              value,
              label: strings.directions[value],
            }))}
            value={direction}
          />
        </div>
        <fieldset className="space-y-2">
          <Checkbox checked={ruled} label={strings.ruled} onChange={setRuled} />
          <Checkbox
            checked={nameLine}
            label={strings.nameLine}
            onChange={setNameLine}
          />
        </fieldset>
        {parsed.duplicates > 0 ? (
          <Notice>
            {t(strings.repeats, { count: fmt(parsed.duplicates) })}
          </Notice>
        ) : null}
        {parsed.overflow > 0 ? (
          <Notice>{t(strings.overflow, { max: MAX_TERMS })}</Notice>
        ) : null}
        {parsed.clipped > 0 ? (
          <Notice>{t(strings.clipped, { count: fmt(parsed.clipped) })}</Notice>
        ) : null}
      </div>

      {parsed.terms.length === 0 ? (
        <div aria-live="polite" className="print:hidden">
          <Notice>{strings.needTerm}</Notice>
        </div>
      ) : (
        <div className="space-y-6 print:space-y-0">
          <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
            <div aria-live="polite" className="space-y-1">
              <h2 className={WIDGET_H2}>{strings.heading}</h2>
              <p className={HINT}>
                {tPlural(strings.count, locale, parsed.terms.length, {
                  count: fmt(parsed.terms.length),
                })}
              </p>
            </div>
            <button
              className={BUTTON_PRIMARY}
              onClick={() => window.print()}
              type="button"
            >
              {strings.print}
            </button>
          </div>
          {parsed.terms.map((term, index) => (
            <FrayerSheet
              headings={headings}
              key={`${term}-${index.toString()}`}
              model={index === 0 && hasModel(model) ? model : null}
              nameLine={nameLine}
              ratio={ratio}
              ruled={ruled}
              strings={strings}
              term={term}
            />
          ))}
        </div>
      )}
    </div>
  );
}
